import React, { useState, useRef, useMemo } from 'react';
import { Controller } from 'react-hook-form';

import { Form, MultiSelect, Dropdown, SelectOption } from 'common.ui';
import { ReactComponent as IconCross } from 'assets/icons/cross-icon.svg';

import { RoleResponse } from 'api/adgang/models';
import './RolesMultiSelect.scss';

export type RolesMultiSelectOption = {
  roleId: string;
  roleName: string;
  applicationName?: string;
};

type RolesMultiSelectProps<T extends object> = {
  applicationOptions: SelectOption[];
  roles: RoleResponse[];
  name: string;
  label: string;
  okLabel: string;
  form: Form<T>;
  onChange?: (model: string[]) => void;
  onSelectionChange?: (val: string[]) => void;
  ignoreClicksOutside?: boolean;

  onClose?: () => void;
  triggerOnChangeAlways?: boolean; // Trigger onChange when closing even if values are the same
  isInitiallyOpen?: boolean; // MultiSelect is initally open and options expanded
};

function RolesMultiSelect<T extends object>({
  applicationOptions,
  roles,
  form,
  name,
  label,
  okLabel,
  triggerOnChangeAlways,
  isInitiallyOpen,
  onSelectionChange,
  onChange: onChangeCallBack,
  onClose,
  ignoreClicksOutside
}: RolesMultiSelectProps<T>) {
  const [currentApplicationId, setCurrentApplicationId] = useState<string>('global');
  const currentOptions = useMemo<SelectOption[]>(
    () =>
      roles
        .filter((o) => (o.applicationId || 'global') === currentApplicationId)
        .map(
          (o) =>
            ({
              id: o.roleId?.toString() ?? '',
              text: o.name ?? ''
            } as SelectOption)
        ),
    [roles, currentApplicationId]
  );

  const containerRef = useRef(null);

  return (
    <div className='roles-multiselect-container' ref={containerRef}>
      <IconCross style={{ float: 'right', width: '8px' }} onClick={onClose} className='close-multiselect' />
      <b>Filtrer utvalg på:</b>
      <div style={{ marginTop: '1.4em' }}>
        <Dropdown
          id='appfilter'
          labelText='Applikasjon'
          value={currentApplicationId}
          options={applicationOptions}
          onChange={(e: React.ChangeEvent<HTMLSelectElement>) => {
            const applicationId = e.target.value;
            setCurrentApplicationId(applicationId);
          }}
        />
      </div>

      <Controller
        // @ts-expect-error react-hook-form v6 Controller typer ikke generisk skjemamodell
        defaultValue={form.propsform.model && form.propsform.model[name]}
        control={form.control}
        name={name}
        // key={currentApplicationId || ''}
        render={({ onChange, value }) => (
          <MultiSelect
            label={label}
            okLabel={okLabel}
            id={name}
            triggerOnChangeAlways={triggerOnChangeAlways}
            isInitiallyOpen={isInitiallyOpen}
            ignoreClicksOutside={ignoreClicksOutside}
            onSelectionChange={onSelectionChange}
            onChange={(model: string[]) => {
              onChange(model);
              if (onChangeCallBack) onChangeCallBack(model);
            }}
            options={currentOptions}
            selectedIds={value}
            doNotDisplayOptionsOnTop
            wrapperRef={containerRef}
          />
        )}
      />
    </div>
  );
}

export { RolesMultiSelect };
